import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Plus, Clock, Wrench, Loader2, ChevronRight } from 'lucide-react';
import { getFFEGuidance } from '../../../../config/guidance/ffe.guidance';
import { getLSAGuidance } from '../../../../config/guidance/lsa.guidance';
import { getNAVGuidance } from '../../../../config/guidance/nav.guidance';
import { getGMDSSGuidance } from '../../../../config/guidance/gmdss.guidance';
import { getElectricalGuidance } from '../../../../config/guidance/electrical.guidance';
import { getMedicalGuidance } from '../../../../config/guidance/medical.guidance';
import { getEnvironmentalGuidance } from '../../../../config/guidance/environmental.guidance';
import { getOperationsGuidance } from '../../../../config/guidance/operations.guidance';
import { useStorageState } from '../../../../hooks/useStorageState';
import { filterTombstoned } from '../../../../sync/tombstone';  // FT-458: hide soft-deleted defects from equipment view
import { useVessel } from '../../../../contexts/VesselContext';
import { useAuth } from '../../auth/AuthGate';
import { summarizeJob } from '../../../../services/equipmentMaintenanceService';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
// FT-365 v3.1 hotfix: self-healing raw reader for currentVesselId.
import { readCurrentVesselIdRaw } from '../../../../utils/readCurrentVesselId';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. EquipmentDetailHeader's raw ArrowLeft back button removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { useCrew } from '../../../../hooks/useCrew';
import { useFileUpload } from '../../../../hooks/useFileUpload';
import { useAuditTrail } from '../../../../hooks/useAuditTrail';
import { FEATURE_LINK_DEFECTS_TO_EQUIPMENT } from '../../../../config/featureFlags';
import EquipmentDetailHeader from './EquipmentDetailHeader';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
import { toShortEquipId } from '../../../../utils/equipmentId';
// FT-189: Centralized equipment name resolution
import { getEquipmentDisplayName } from '../../../../services/equipment/utils/equipmentRow';
// QuickDefectModal removed - now navigates to full RecordDefectView in Defects tab
import DocumentPreviewModal from '../../../common/DocumentPreviewModal';
import DocumentsTab from './DocumentsTab';
import EquipmentQuickStats from './EquipmentQuickStats';
import EquipmentDetailsCard from './EquipmentDetailsCard';
import EquipmentNotesCard from './EquipmentNotesCard';
// FT-531: reuse the Add Equipment form's exact Category/Location control
// type (free-text + fixed suggestion list) and options for editing, rather
// than inventing a different UI pattern.
import { EQUIPMENT_CATEGORIES, COMMON_LOCATIONS } from './AddEquipmentView';
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { useMaintenanceHistory } from '../../../../hooks/domain/maintenance/useMaintenanceHistory';
import MaintenanceCompletionReport from './MaintenanceCompletionReport';

// FT-531 — field-level equipment edit audit logging.
//
// Human-readable labels for the reason shown in the audit entry. Keyed by
// the CANONICAL audited field name (not the legacy mirror keys like
// serialNumber/specifications — see AUDIT_EXCLUDED... note below).
const FIELD_LABELS = {
  notes: 'Notes',
  serialNo: 'Serial Number',
  modelType: 'Model/Type',
  category: 'Category',
  location: 'Location'
};

// Fields that must NEVER be diffed/audited via this per-field logging path,
// even though they live on the same equipment object. Mostly
// system-managed/read-only/structural fields that are never user-edited
// from this view. This is a sanity-check list, not a loop-based filter —
// handleFieldUpdate only ever receives ONE explicit field name per call
// (serialNo / modelType / category / location / notes), so nothing here can
// accidentally get wired up, but it documents intent for the next person.
// Also deliberately excludes auditLog itself (never diff an item's audit
// history against itself) and lastServiceAt/service-date fields, which are
// auto-populated from Maintenance completions, not edited here (see
// EquipmentDetailsCard.js's read-only "Last Service Date" field).
const AUDIT_EXCLUDED_FIELDS = new Set([
  'id', 'updatedAt', 'updatedBy', 'createdAt', 'createdBy', 'auditLog',
  'variant', 'sourceRequirementIds', 'sourceRequirementId', 'source',
  'shortCode', 'guidanceKey', 'meta', 'lastServiceAt'
]);

// Some fields have a legacy mirror key that may hold the "real" value on
// older items (e.g. an item with serialNumber set but serialNo never
// written). Resolve the effective old value the same way the UI displays
// it, so the audit "before" value matches what the user actually saw.
const FIELD_OLD_VALUE_GETTERS = {
  serialNo: (eq) => (eq.serialNo !== undefined && eq.serialNo !== null && eq.serialNo !== '')
    ? eq.serialNo : eq.serialNumber,
  modelType: (eq) => (eq.modelType !== undefined && eq.modelType !== null && eq.modelType !== '')
    ? eq.modelType : eq.specifications
};

// undefined/null/'' are all treated as "no value" for the purposes of
// deciding whether an edit is a no-op (so we don't log a false-positive
// audit entry when a user clicks into a field and saves without changing
// anything — the exact class of noise that plagued Vessel Settings).
const isEmptyish = (v) => v === undefined || v === null || v === '';

// JSON.stringify SILENTLY DROPS object keys whose value is `undefined`
// (e.g. JSON.stringify({ a: undefined }) === '{}') — which would make the
// CSV export (auditExportCSV.js, JSON.stringify(entry.changes)) quietly
// lose the field name entirely for a sparse "never set" value, not just
// show it oddly. Normalizing undefined -> null here keeps the key (and its
// "no value" meaning) intact through JSON.stringify, while leaving an
// explicit empty string ('') untouched/distinct — a user-cleared field is
// not the same thing as a field that was never set.
const toAuditValue = (v) => (v === undefined ? null : v);

const EquipmentDetailView = ({
  equipment: equipmentProp,
  onBack,
  onUpdate,
  onDelete,
  onAfterDelete,
  setActiveModule,
  setActiveModuleWithTab,
  isUpdating = false,
  isDeleting = false
}) => {
  // Add swipe-to-go-back functionality
  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  // FT-356c: register parent's onBack as the shell-level back handler.
  useSetBackHandler(onBack);

  // Get current vessel ID for scoped storage (with fallback to direct read).
  // FT-365 v3.1 hotfix: self-healing raw reader unwraps legacy double-JSON
  // encoding left over from a brief FT-365 v3 exposure.
  const { currentVesselId: contextVesselId, getCurrentVessel } = useVessel();
  const currentVesselId = contextVesselId || readCurrentVesselIdRaw();

  // FT-345: captured-by attribution for <PhotoAttachment>
  const { user: currentUser } = useAuth();

  // Load maintenance jobs using correct vessel-scoped key
  const jobsKey = `maintenance_jobs_${currentVesselId}`;
  const savedJobs = useMemo(() => {
    if (!currentVesselId) return [];
    const data = localStorage.getItem(jobsKey);
    return data ? JSON.parse(data) : [];
  }, [currentVesselId, jobsKey]);

  const { value: maintenanceHistory = {} } = useStorageState('maintenance_history', {});
  const { value: defects = [] } = useStorageState('defects', []);
  // FT-459: also pull the dialog HOSTS (<ConfirmDialog/>, <AlertDialog/>) from
  // the SAME useCustomDialogs() instance. showAlert sets state on this
  // instance, so its hosts must be mounted in this component's JSX or the
  // alert never renders. (ConfirmDialog is kept mounted for any future
  // showConfirm use — FT-531 removed the one caller that used it, the dead
  // handleSerialNumberChange confirmation.)
  const { showAlert, ConfirmDialog, AlertDialog } = useCustomDialogs();
  const { logChange, showDeleteDialog, canDelete, canHardDelete, DeleteModal } = useAuditTrail('equipment');
  const [selectedTab, setSelectedTab] = useState(0);
  // showDefectModal state removed - now navigates to Defects tab
  const { crew } = useCrew();

  // Completion report state
  const [selectedCompletion, setSelectedCompletion] = useState(null);
  const [showCompletionReport, setShowCompletionReport] = useState(false);
  const { items: allEquipment, updateItem, archiveItem, removeItem } = useEquipmentRegister();
  const { getHistoryForEquipmentId } = useMaintenanceHistory();

  // Local state for equipment to reflect updates immediately
  const [equipment, setEquipment] = useState(equipmentProp);

  // FT-531 fix-first (Bug 1 — stale whole-object writes): a ref that's ALWAYS
  // the latest known equipment state, kept in sync both by the effect below
  // AND synchronously inside handleFieldUpdate itself (so a second field-save
  // landing in the same tick sees the first one's result immediately, without
  // waiting for a re-render). handleFieldUpdate reads its "before" value and
  // builds its audit snapshot from THIS ref, never from the `equipment`
  // variable closed over at render time — a debounced save's setTimeout
  // closure can fire using an arbitrarily old render's `handleFieldUpdate`,
  // and that old closure's `equipment` variable is frozen forever at
  // whatever it was when that render happened. Reading through a ref
  // sidesteps that staleness entirely.
  const equipmentRef = useRef(equipment);

  // FT-531: Category/Location edit state. These were DISPLAY-ONLY before
  // FT-531 (genuine scope expansion, approved 2026-10-26 — e.g. flares
  // miscategorized under GMDSS instead of LSA previously meant
  // delete-and-recreate the whole record to fix). They reuse the Add
  // Equipment form's free-text-input-with-suggestions control type (see
  // AddEquipmentView.js) and debounce 1000ms before saving, same as
  // Serial/Model/Notes — these are plain text fields with a suggestion
  // dropdown, NOT a native <select>, so per-keystroke saving is just as
  // unworkable here as it was for Serial/Model.
  const [categoryInput, setCategoryInput] = useState(
    EQUIPMENT_CATEGORIES.find(c => c.value === equipmentProp.category)?.label || equipmentProp.category || ''
  );
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryTimeoutRef = useRef(null);
  // FT-531 fix-first (Bug 2 — lost edits on unmount): holds the resolved
  // category value that WOULD be saved when the pending timeout fires, so a
  // flush (unmount or blur, before the timer naturally fires) can save it
  // immediately instead of the edit being silently dropped. null = nothing
  // pending.
  const pendingCategoryRef = useRef(null);

  const [locationInput, setLocationInput] = useState(equipmentProp.location || '');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const locationTimeoutRef = useRef(null);
  const pendingLocationRef = useRef(null);

  // Get maintenance history for this specific equipment item
  const historyForItem = getHistoryForEquipmentId(equipment?.id || '');
  
  // Helper to sort and get last maintenance record
  const getLastMaintenanceRecord = (history) => {
    if (!history || history.length === 0) return null;
    return history
      .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
      .slice(0, 1)[0] || null;
  };
  
  const lastRecord = getLastMaintenanceRecord(historyForItem);

  // Helper to resolve crew ID to display name
  const getCrewDisplayName = (crewId, crewList) => {
    if (!crewId || !crewList || !Array.isArray(crewList)) {
      return crewId || 'Not recorded';
    }
    
    const crewMember = crewList.find(c => c.id === crewId);
    if (crewMember) {
      return crewMember.full_name || crewMember.name || crewId;
    }
    
    return crewId; // Return original ID if no match found
  };

  // Helper to format dates for display
  const formatHistoryDate = (date) => {
    if (!date) return 'Date not recorded';
    try {
      return new Date(date).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return date;
    }
  };

  // Helper to truncate notes
  const truncateNotes = (notes, maxLength = 80) => {
    if (!notes) return '—';
    if (notes.length <= maxLength) return notes;
    return notes.substring(0, maxLength).trim() + '…';
  };

  // Phase 5: Use properly memoized file upload hook
  const {
    documents,
    uploading,
    uploadFile,
    deleteFile,
    renameFile,
    downloadFile
  } = useFileUpload(equipment?.id, 'equipment');

  const [previewDocument, setPreviewDocument] = useState(null);

  // Sync local equipment state with prop changes.
  //
  // FT-531 KNOWN LIMITATION (documented, not actively fixed — Jonathan's
  // explicit call): if a background sync pull updates equipmentProp while a
  // user has this detail view open mid-edit (e.g. mid-debounce on Serial
  // Number), this effect will overwrite local `equipment` state with the
  // incoming prop, which could in theory clobber an in-progress local edit
  // before its debounced save fires. This is the same risk class as the
  // Vessel Settings saga's rounds 10-11 (a timing race between an external
  // data refresh and a captured "before" baseline), just triggered by sync
  // instead of cross-device hydration. No in-flight-save guard or prop-
  // update-ignoring has been added here — see FT-531 ticket for context on
  // why this was deliberately left as a documented risk rather than built
  // against.
  useEffect(() => {
    setEquipment(equipmentProp);
  }, [equipmentProp]);

  // FT-531 fix-first: keep equipmentRef in sync with every equipment state
  // change (this effect is the "ordinary" path; handleFieldUpdate ALSO
  // updates the ref synchronously for same-tick correctness — see there).
  useEffect(() => {
    equipmentRef.current = equipment;
  }, [equipment]);

  useEffect(() => {
    if (equipment) {
      // Generate short code if it doesn't exist
      if (!equipment.shortCode) {
        equipment.shortCode = toShortEquipId(equipment.id);
      }
    }
  }, [equipment]);

  // FT-531: keep Category/Location input buffers in sync with the
  // underlying equipment state (e.g. after a save resolves, or if the prop
  // changes from elsewhere) — same pattern as EquipmentNotesCard/
  // EquipmentDetailsCard's local-buffer sync effects.
  useEffect(() => {
    setCategoryInput(EQUIPMENT_CATEGORIES.find(c => c.value === equipment.category)?.label || equipment.category || '');
  }, [equipment.category]);

  useEffect(() => {
    setLocationInput(equipment.location || '');
  }, [equipment.location]);

  // FT-531 fix-first (Bug 2): flush (not merely clear) pending Category/
  // Location debounce timers on unmount. See the flushCategory/flushLocation
  // definitions and the useEffect that wires this cleanup, further down near
  // the rest of the Category/Location handlers — kept together so the flush
  // logic lives next to the save logic it mirrors.

  if (!equipment) return null;

  // Find guidance for this equipment (clean architecture - all domains use guidanceKey)
  const ffeGuidance = equipment.guidanceKey ? getFFEGuidance(equipment.guidanceKey) : null;
  const lsaGuidance = equipment.guidanceKey ? getLSAGuidance(equipment.guidanceKey) : null;
  const navGuidance = equipment.guidanceKey ? getNAVGuidance(equipment.guidanceKey) : null;
  const gmdssGuidance = equipment.guidanceKey ? getGMDSSGuidance(equipment.guidanceKey) : null;
  const electricalGuidance = equipment.guidanceKey ? getElectricalGuidance(equipment.guidanceKey) : null;
  const medicalGuidance = equipment.guidanceKey ? getMedicalGuidance(equipment.guidanceKey) : null;
  const environmentalGuidance = equipment.guidanceKey ? getEnvironmentalGuidance(equipment.guidanceKey) : null;
  const operationsGuidance = equipment.guidanceKey ? getOperationsGuidance(equipment.guidanceKey) : null;
  
  const guidance = ffeGuidance || lsaGuidance || navGuidance || gmdssGuidance || electricalGuidance || medicalGuidance || environmentalGuidance || operationsGuidance;

  // Simplified to never show Guidance tab (MVP approach)
  const tabs = ['Details', 'History', 'Documents'];

  // Debug logging for navigation/GMDSS equipment or when no guidance found
  const guidanceKey = (equipment.guidanceKey || '').toLowerCase();
  if (!guidance || (equipment.guidanceKey && (
    guidanceKey.includes('nav') ||
    guidanceKey.includes('gps') ||
    guidanceKey.includes('radar') ||
    guidanceKey.includes('ais') ||
    guidanceKey.includes('compass') ||
    guidanceKey.includes('gmdss') ||
    guidanceKey.includes('vhf') ||
    guidanceKey.includes('epirb') ||
    guidanceKey.includes('sart') ||
    equipment.name?.toLowerCase().includes('gps') ||
    equipment.name?.toLowerCase().includes('radar') ||
    equipment.name?.toLowerCase().includes('ais') ||
    equipment.name?.toLowerCase().includes('compass') ||
    equipment.name?.toLowerCase().includes('vhf') ||
    equipment.name?.toLowerCase().includes('epirb') ||
    equipment.name?.toLowerCase().includes('sart')
  ))) {

    if (navGuidance) {

    }
    if (gmdssGuidance) {

    }
  }
  
  // Filter jobs that are linked to THIS equipment
  // Jobs have linkedEquipmentIds[] array, not the other way around
  const linkedJobs = useMemo(() => {
    return savedJobs.filter(job =>
      job.linkedEquipmentIds?.includes(equipment.id)
    );
  }, [savedJobs, equipment.id]);

  // Enrich jobs with summary data using READ ONLY pattern
  // This calculates lastCompleted and nextDue from job.completions[] without writing to equipment
  const linkedJobsWithSummary = useMemo(() => {
    if (!linkedJobs || linkedJobs.length === 0) return [];

    // Create equipment map for summarizeJob helper
    const equipmentMap = new Map([[equipment.id, equipment]]);

    // Enrich each job with summary (lastCompleted, nextDue, status)
    return linkedJobs.map(job => ({
      ...job,
      summary: summarizeJob(job, equipmentMap)
    }));
  }, [linkedJobs, equipment]);

  const isRequiredEquipment = equipment.source === 'requirements';

  // Get maintenance history for this equipment
  const equipmentHistory = Object.entries(maintenanceHistory)
    .filter(([jobId]) => {
      // Match by equipment type or ID
      const job = savedJobs.find(j => j.id === jobId);
      return job && (
        job.equipmentType?.toLowerCase().includes(equipment.type?.toLowerCase() || '') ||
        job.linkedEquipmentIds?.includes(equipment.id)
      );
    })
    .map(([jobId, history]) => ({
      jobId,
      ...history,
      job: savedJobs.find(j => j.id === jobId)
    }))
    .sort((a, b) => new Date(b.completedAt || b.date) - new Date(a.completedAt || a.date));

  // Get linked defects for this equipment (updated to use equipment_id)
  // FT-458: exclude soft-deleted (tombstoned) defects from the equipment view.
  const linkedDefects = filterTombstoned(defects).filter(defect =>
    defect.equipment_id === equipment.id ||      // NEW field (primary)
    defect.equipmentId === equipment.id ||       // Legacy prefill field
    defect.linkedEquipment?.includes(equipment.id)  // Legacy array field
  );

  // Handler for stat card navigation
  const handleStatNavigation = (target) => {
    if (target === 'history') {
      // Navigate to History tab (now always index 1)
      setSelectedTab(1);
    } else if (target === 'defects') {
      // Open defect modal or navigate to defects
      handleLogDefect();
    }
  };

  // Handler for field updates.
  //
  // FT-531: now the single place that logs field-level equipment edit audit
  // entries. Called once per actual field save — NEVER also from
  // EquipmentRecordsTab.js's handleUpdateEquipment, which would double-log
  // (see FT-531 brief re: the double updateItem() call already present via
  // onUpdate -> handleUpdateEquipment -> updateItem()).
  //
  // `extraFields` carries legacy mirror keys that must be written alongside
  // the canonical audited field (serialNumber alongside serialNo,
  // specifications alongside modelType) WITHOUT those mirror keys being
  // treated as separately-audited fields — only `field`/`value` is diffed.
  //
  // This is a PER-FIELD diff (just the one field that changed in this save
  // event), not a whole-object diff — this UX has no single multi-field
  // Save button, so the Vessel Settings "diff the whole form" pattern does
  // not apply here. This is a SEPARATE, NEW logging path alongside the
  // pre-existing auditUtils.js/createAuditEntry mechanism that updateItem()
  // already runs on every update (writes to item.auditLog) — that mechanism
  // is untouched by FT-531; logChange below feeds the actual "Equipment
  // Change History" screen (ChangeLogViewer.js).
  const handleFieldUpdate = async (field, value, extraFields = null) => {
    try {
      // FT-531 fix-first (Bug 1): read the "before" baseline from the ALWAYS-
      // latest ref, never from the `equipment` variable closed over at this
      // function's render time — see equipmentRef's declaration comment.
      const baseline = equipmentRef.current;
      const oldValueGetter = FIELD_OLD_VALUE_GETTERS[field];
      const oldValue = oldValueGetter ? oldValueGetter(baseline) : baseline[field];

      // FT-531 fix-first (Bug 1 — stale whole-object writes): send ONLY the
      // DELTA for this save, never a full `{...equipment, ...}` spread of a
      // possibly-stale snapshot. Two fields can be edited within ~1s of each
      // other (e.g. Serial then Model) such that the SECOND save's closure
      // still holds an OLD copy of the first field's value — spreading that
      // stale copy into the write would silently revert the first field's
      // already-saved new value, even though its audit entry already claims
      // the change happened. updateItem()/onUpdate() both merge the delta
      // against the LATEST stored item, so a delta-only write is correct
      // regardless of what else changed in between.
      const delta = {
        [field]: value,
        ...(extraFields || {}),
        ...(field === 'notes' ? { notesUpdatedAt: new Date().toISOString() } : {})
      };

      // A locally-merged view for the audit entry's `item` snapshot/display
      // only — built from the fresh baseline, never written to storage.
      const updatedEquipment = { ...baseline, ...delta };

      // Keep the ref correct SYNCHRONOUSLY (don't wait for the effect/
      // re-render) so a second field-save landing in the same tick uses
      // THIS save's result as its own baseline.
      equipmentRef.current = updatedEquipment;

      // Update via hook (saves to localStorage) — DELTA ONLY.
      await updateItem(equipment.id, delta);

      // Update local state immediately — MERGE, never replace.
      setEquipment(prev => ({ ...prev, ...delta }));

      // Also call parent onUpdate if provided — DELTA ONLY, same reasoning.
      if (onUpdate) {
        onUpdate(equipment.id, delta);
      }

      // No-op guard: skip the audit entry entirely if the value didn't
      // actually change (undefined/null/'' all treated as equivalent "no
      // value" for THIS comparison only — the entry we log, if we log one,
      // still keeps the real raw oldValue/newValue so a genuine
      // undefined-> real-value transition is captured correctly below).
      const noOp = oldValue === value || (isEmptyish(oldValue) && isEmptyish(value));
      if (!AUDIT_EXCLUDED_FIELDS.has(field) && !noOp) {
        await logChange({
          action: 'update',
          item: updatedEquipment,
          changes: {
            before: { [field]: toAuditValue(oldValue) },
            after: { [field]: toAuditValue(value) }
          },
          reason: { code: 'edit', label: `${FIELD_LABELS[field] || field} updated` }
        });
      }

      showAlert(`${field === 'notes' ? 'Notes' : 'Field'} updated successfully`);
    } catch (error) {

      showAlert('Failed to update. Please try again.');
    }
  };

  // FT-531 fix-first: a ref to the LATEST handleFieldUpdate, so the
  // Category/Location flush-on-unmount effect (declared once, deps []) can
  // always invoke the current version rather than one frozen at mount time.
  // (handleFieldUpdate itself no longer depends on render-time closures for
  // correctness — it reads equipmentRef — so this is purely about always
  // having a live, callable reference at flush time.)
  const handleFieldUpdateRef = useRef(null);
  handleFieldUpdateRef.current = handleFieldUpdate;

  // Handler for notes updates
  const handleNotesUpdate = async (newNotes) => {
    await handleFieldUpdate('notes', newNotes);
  };

  // FT-531: Category input change — debounced free-text w/ suggestions,
  // same control type as the Add Equipment form (AddEquipmentView.js).
  // Resolves typed text to a known category's `value` when it matches a
  // known label (case-insensitive), otherwise stores the raw typed text
  // directly (equivalent to what AddEquipmentView ultimately persists for
  // its 'Other' + customCategory path, without needing that indirection here).
  const handleCategoryInputChange = (e) => {
    const inputValue = e.target.value;
    setCategoryInput(inputValue);

    const matched = EQUIPMENT_CATEGORIES.find(c => c.label.toLowerCase() === inputValue.trim().toLowerCase());
    const resolvedCategory = matched ? matched.value : inputValue.trim();
    // FT-531 fix-first (Bug 2): remember what WOULD be saved so a flush
    // (unmount or blur, below) can save it immediately if the 1000ms timer
    // never gets the chance to fire naturally.
    pendingCategoryRef.current = { resolvedCategory };

    if (categoryTimeoutRef.current) clearTimeout(categoryTimeoutRef.current);
    categoryTimeoutRef.current = setTimeout(() => {
      categoryTimeoutRef.current = null;
      pendingCategoryRef.current = null;
      handleFieldUpdateRef.current('category', resolvedCategory);
    }, 1000);
  };

  const selectCategoryOption = (cat) => {
    if (categoryTimeoutRef.current) clearTimeout(categoryTimeoutRef.current);
    categoryTimeoutRef.current = null;
    pendingCategoryRef.current = null;
    setCategoryInput(cat.label);
    setShowCategoryDropdown(false);
    handleFieldUpdate('category', cat.value);
  };

  // FT-531 fix-first (Bug 2): flush — save a pending Category edit RIGHT NOW
  // instead of discarding it. Used on unmount (navigating away within the
  // 1000ms window) and on blur (so a visible "move on" saves sooner than
  // waiting for the timer).
  const flushCategory = () => {
    if (categoryTimeoutRef.current) {
      clearTimeout(categoryTimeoutRef.current);
      categoryTimeoutRef.current = null;
    }
    if (pendingCategoryRef.current) {
      const { resolvedCategory } = pendingCategoryRef.current;
      pendingCategoryRef.current = null;
      handleFieldUpdateRef.current('category', resolvedCategory);
    }
  };

  // FT-531: Location input change — same pattern, free text with a
  // suggestion list (not a fixed enum — commonLocations are suggestions
  // only, matching AddEquipmentView's location field exactly).
  const handleLocationInputChange = (e) => {
    const value = e.target.value;
    setLocationInput(value);

    const trimmed = value.trim();
    pendingLocationRef.current = { value: trimmed };

    if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
    locationTimeoutRef.current = setTimeout(() => {
      locationTimeoutRef.current = null;
      pendingLocationRef.current = null;
      handleFieldUpdateRef.current('location', trimmed);
    }, 1000);
  };

  const selectLocationOption = (loc) => {
    if (locationTimeoutRef.current) clearTimeout(locationTimeoutRef.current);
    locationTimeoutRef.current = null;
    pendingLocationRef.current = null;
    setLocationInput(loc);
    setShowLocationDropdown(false);
    handleFieldUpdate('location', loc);
  };

  // FT-531 fix-first (Bug 2): same flush pattern as Category, for Location.
  const flushLocation = () => {
    if (locationTimeoutRef.current) {
      clearTimeout(locationTimeoutRef.current);
      locationTimeoutRef.current = null;
    }
    if (pendingLocationRef.current) {
      const { value } = pendingLocationRef.current;
      pendingLocationRef.current = null;
      handleFieldUpdateRef.current('location', value);
    }
  };

  // FT-531 fix-first (Bug 2): FLUSH pending Category/Location saves on
  // unmount, instead of merely clearing the timer and dropping the edit.
  // Before this fix, typing into Category/Location then tapping Back (or
  // switching equipment items) within 1000ms silently lost the edit — no
  // save, no audit entry, nothing. This is a REGRESSION class specifically
  // introduced by debouncing (these fields were display-only before FT-531,
  // so there was nothing to lose).
  useEffect(() => {
    return () => {
      flushCategory();
      flushLocation();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FT-531: EquipmentDetailsCard's Serial Number / Model fields now call
  // onFieldSave(field, value, extraFields) after their own 1000ms debounce
  // — route straight through the same audited handleFieldUpdate used by
  // everything else in this view (replaces the old handleSimpleEquipmentSave,
  // which saved a merged object with no field-name context to audit against).
  const handleEquipmentDetailsFieldSave = (field, value, extraFields) => {
    handleFieldUpdate(field, value, extraFields);
  };

  const handleLogDefect = () => {
    if (!FEATURE_LINK_DEFECTS_TO_EQUIPMENT) {
      showAlert('Feature flag disabled: FEATURE_LINK_DEFECTS_TO_EQUIPMENT');
      return;
    }

    // Navigate to Defects tab with RecordDefectView open and equipment pre-filled
    // Create prefill data matching DEFECTS_LINKING_SPEC format
    const prefillData = {
      version: "1.0",
      source: "equipment-register",
      timestamp: Date.now(),
      equipmentId: equipment.id,
      equipmentName: equipment.name || equipment.label || equipment.description,
      location: equipment.location || '',
      category: equipment.category || ''
    };

    // Store prefill data for DefectsModule to read
    sessionStorage.setItem('defects:newPrefill', JSON.stringify(prefillData));

    // Set module tab to trigger record view
    sessionStorage.setItem('moduleTab_defects', 'record');

    // Store return context - so user comes back to THIS equipment detail
    // This is used by DefectsModule's onBack handler
    sessionStorage.setItem('defectReturnContext', JSON.stringify({
      returnModule: 'equipment',
      returnView: 'detail',
      equipmentId: equipment.id,
      equipmentData: {
        id: equipment.id,
        name: equipment.name || equipment.label || equipment.description,
        category: equipment.category,
        location: equipment.location
      }
    }));

    // Navigate to standalone Defects module
    setActiveModule('defects');

    // Dispatch event after a small delay to let DefectsModule render and check for prefill data
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('defects:checkPrefill'));
    }, 100);
  };

  const handleViewMaintenance = () => {
    // Navigate to Maintenance Schedule tab with equipment filter
    // Store filter info in sessionStorage so PlannedMaintenanceTab can pick it up
    sessionStorage.setItem('maintenanceEquipmentFilter', JSON.stringify({
      equipmentId: equipment.id,
      equipmentName: equipment.name || equipment.item_name || 'Equipment'
    }));

    // Navigate to maintenance module, schedule tab
    if (setActiveModuleWithTab) {
      setActiveModuleWithTab('maintenance', 'schedule');
    } else if (setActiveModule) {
      setActiveModule('maintenance');
    }
  };

  // Handler for viewing a completion record from service history
  const handleViewCompletionRecord = (record) => {
    // Get vessel name from context
    const vessel = getCurrentVessel?.();
    const vesselName = vessel?.name || '';

    // Enrich the record with equipment and vessel info
    const enrichedRecord = {
      ...record,
      equipmentNames: [equipment.name || equipment.label || equipment.description || 'Equipment'],
      vesselName: vesselName
    };
    setSelectedCompletion(enrichedRecord);
    setShowCompletionReport(true);
  };

  // Handler for editing a completion record from the completion report
  const handleEditCompletionFromEquipment = (completion, reason) => {
    // Log for now - full edit form in future phase
    console.log('[EquipmentDetailView] Edit requested:', completion.id, 'Reason:', reason);
    showAlert(`Edit reason captured: "${reason}"\n\nNote: Full edit form coming in a future update.`);
  };

  const handleDelete = async () => {
    // Only allow delete for manually added equipment
    if (equipment.source === 'requirements') {
      showAlert('Cannot delete required equipment. Required equipment is managed through vessel settings.');
      return;
    }

    // Check permissions
    if (!canDelete) {
      showAlert('You do not have permission to delete equipment.');
      return;
    }

    // Show delete dialog with reason capture.
    // FT-511 fix: do archive directly here — do NOT call onDelete(id) inside
    // the callback. onDelete = handleDeleteEquipment in the parent, which also
    // calls showDeleteDialog. When the detail view is open, the parent is in
    // early-return mode so its <DeleteModal> is not mounted → calling its
    // showDeleteDialog produces silence. Using our own showDeleteDialog (whose
    // <DeleteModal> is mounted at line 803) and archiving directly avoids the
    // double-dialog and the unmounted-modal problem.
    const reasonData = await showDeleteDialog(equipment, async (reason) => {
      await logChange({
        action: 'delete',
        item: equipment,
        reason: reason.reason,
        authorizedBy: reason.authorizedBy,
        deleteType: reason.deleteType
      });

      if (reason.deleteType === 'hard') {
        await removeItem(equipment.id);
      } else {
        await archiveItem(equipment.id, reason.reason || 'User deleted');
      }

      return true;
    });

    if (reasonData) {
      showAlert(reasonData.deleteType === 'hard' ? 'Equipment permanently deleted' : 'Equipment archived successfully');
      if (onAfterDelete) onAfterDelete();
    }
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(equipment.id);
      showAlert('Equipment ID copied to clipboard');
    } catch (err) {

      showAlert('Failed to copy ID');
    }
  };

  // Phase 5: File upload handlers using the hook
  const handleFileUpload = async (file) => {
    try {
      await uploadFile(file);
      showAlert(`File "${file.name}" uploaded successfully`);
    } catch (err) {
      showAlert(`Upload failed: ${err.message}`);
    }
  };

  const handleFileDelete = (documentId) => {
    deleteFile(documentId);
    showAlert('Document deleted');
  };

  const handleFileRename = (documentId, newName) => {
    renameFile(documentId, newName);
  };

  const handleFileDownload = (doc) => {
    downloadFile(doc);
  };

  // FT-345: photo evidence update handler. Called by <DocumentsTab> when
  // <PhotoAttachment> emits an onChange (add / delete / caption). Runs
  // through the same updateItem() path as every other equipment mutation so
  // audit-log + storage stays consistent.
  const handleUpdateEquipmentFromDocs = async (nextEquipment) => {
    if (!nextEquipment?.id) return;
    try {
      await updateItem(nextEquipment.id, nextEquipment);
      setEquipment(nextEquipment);
      if (onUpdate) {
        onUpdate(nextEquipment.id, nextEquipment);
      }
    } catch (err) {
      showAlert(`Failed to save equipment photos: ${err?.message || 'unknown error'}`);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = () => {
    const lastService = equipment.lastServiceAt ? new Date(equipment.lastServiceAt) : null;
    const daysSinceService = lastService ? 
      Math.floor((new Date() - lastService) / (1000 * 60 * 60 * 24)) : null;
    const serviceStatus = !lastService ? 'unknown' :
                        daysSinceService > 365 ? 'overdue' :
                        daysSinceService > 335 ? 'due_soon' : 'ok';
    
    const badges = {
      'ok': { label: 'OK', color: 'bg-green-100 text-green-800' },
      'due_soon': { label: 'Service Due Soon', color: 'bg-yellow-100 text-yellow-800' },
      'overdue': { label: 'Service Overdue', color: 'bg-red-100 text-red-800' },
      'unknown': { label: 'No Service Data', color: 'bg-gray-100 text-gray-800' }
    };
    
    return badges[serviceStatus];
  };

  const statusBadge = getStatusBadge();

  // Prepare chips for header
  const chips = [
    `Source: ${(equipment.source === 'requirements' || equipment.sourceRequirementId) ? 'Required' : 'Added'}`,
    equipment.category ? equipment.category.toUpperCase() : null,
    equipment.location ? equipment.location.charAt(0).toUpperCase() + equipment.location.slice(1).replace(/_/g, ' ') : null,
    statusBadge.label !== 'OK' && statusBadge.label !== 'No Service Data' ? statusBadge.label : null
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-100" ref={containerRef}>
      {/* Swipe Indicator */}
      <SwipeIndicator progress={swipeProgress} />

      {/* Compact Header
          FT-356c: onBack no longer passed — bottom bar owns Back navigation. */}
      <EquipmentDetailHeader
        name={getEquipmentDisplayName(equipment)}
        chips={chips}
        shortId={equipment.shortCode}
        onCopyId={handleCopyId}
        onLogDefect={handleLogDefect}
        onViewMaint={handleViewMaintenance}
        onDelete={equipment.source !== 'requirements' && !equipment.sourceRequirementId ? handleDelete : null}
        activeTab={selectedTab}
        onTabChange={setSelectedTab}
        tabs={tabs}
      />
      
      {/* Content with tighter spacing */}
      <div className="max-w-6xl mx-auto px-3 md:px-6 py-3 md:py-4">
        <div className="space-y-4 md:space-y-6">

          {/* Tab Content */}
          <div className="scroll-mt-24 md:scroll-mt-28">
            {/* Details Tab */}
            {selectedTab === 0 && (
                <div className="space-y-6">
                  {/* Quick Stats - At the top */}
                  <EquipmentQuickStats
                    equipment={equipment}
                    maintenanceHistory={historyForItem}
                    defects={linkedDefects}
                    linkedJobs={linkedJobsWithSummary}
                    onNavigate={handleStatNavigation}
                  />

                  {/* Key Information with compact styling */}
                  <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
                    <div className="bg-slate-700 px-5 py-3">
                      <h3 className="text-base font-semibold text-white">Key Information</h3>
                    </div>
                    <div className="px-3 md:px-4 py-3 md:py-4">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 md:grid-cols-4">
                      {/* Category — FT-531: now editable. Was DISPLAY-ONLY
                          before; reuses AddEquipmentView's free-text +
                          suggestion-dropdown control type and options. */}
                      <div className="min-w-0 relative">
                        <dt className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Category</dt>
                        <input
                          type="text"
                          value={categoryInput}
                          onChange={handleCategoryInputChange}
                          onFocus={() => setShowCategoryDropdown(true)}
                          onBlur={() => { flushCategory(); setTimeout(() => setShowCategoryDropdown(false), 200); }}
                          placeholder="Select or type category"
                          className="w-full px-3 py-2 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-900 truncate focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                        />
                        {showCategoryDropdown && (
                          <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                            <div className="py-1">
                              {EQUIPMENT_CATEGORIES.map(cat => (
                                <button
                                  key={cat.value}
                                  type="button"
                                  onMouseDown={(e) => { e.preventDefault(); selectCategoryOption(cat); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                >
                                  {cat.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Source — read-only, unchanged */}
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Source</dt>
                        <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                          <p className="text-sm text-gray-900 truncate">
                            {(equipment.source === 'requirements' || equipment.sourceRequirementId) ? 'Required' : 'Added'}
                          </p>
                        </div>
                      </div>

                      {/* Location — FT-531: now editable, same pattern as Category */}
                      <div className="min-w-0 relative">
                        <dt className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Location</dt>
                        <input
                          type="text"
                          value={locationInput}
                          onChange={handleLocationInputChange}
                          onFocus={() => setShowLocationDropdown(true)}
                          onBlur={() => { flushLocation(); setTimeout(() => setShowLocationDropdown(false), 200); }}
                          placeholder="e.g., Bridge, Engine Room"
                          className="w-full px-3 py-2 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-900 truncate focus:outline-none focus:ring-2 focus:ring-cyan-600 focus:border-cyan-600"
                        />
                        {showLocationDropdown && COMMON_LOCATIONS.some(loc =>
                          loc.toLowerCase().includes(locationInput.toLowerCase())
                        ) && (
                          <div className="absolute z-50 mt-1 w-full bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 max-h-60 overflow-auto">
                            <div className="py-1">
                              {COMMON_LOCATIONS
                                .filter(loc => loc.toLowerCase().includes(locationInput.toLowerCase()))
                                .map(loc => (
                                  <button
                                    key={loc}
                                    type="button"
                                    onMouseDown={(e) => { e.preventDefault(); selectLocationOption(loc); }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                                  >
                                    {loc}
                                  </button>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Regulation — read-only, unchanged */}
                      <div className="min-w-0">
                        <dt className="text-xs uppercase tracking-wide text-gray-500 font-medium mb-1">Regulation</dt>
                        <div className="px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                          <p className="text-sm text-gray-900 truncate">
                            {equipment.reference || equipment.regulation || equipment.requirement || 'Not specified'}
                          </p>
                        </div>
                      </div>
                    </div>
                    </div>
                  </section>

                  {/* Equipment Details */}
                  <EquipmentDetailsCard
                    equipment={equipment}
                    allEquipment={allEquipment}
                    onFieldSave={handleEquipmentDetailsFieldSave}
                    lastMaintenanceRecord={lastRecord}
                  />

                  {/* Equipment Notes */}
                  <EquipmentNotesCard
                    notes={equipment.notes}
                    onSave={handleNotesUpdate}
                    lastUpdated={equipment.notesUpdatedAt}
                  />

                  {/* WBC3 Reference - Simple label for regulatory compliance */}
                  {(equipment.reference || equipment.regulation || equipment.requirement) && (
                    <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-200 p-4">
                      <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                        <span className="font-semibold text-slate-600">WBC3 Reference:</span>{' '}
                        <span className="text-slate-700">{equipment.reference || equipment.regulation || equipment.requirement}</span>
                      </div>
                    </div>
                  )}


                  {/* Linked Maintenance - Only show if they exist */}
                  {linkedJobs.length > 0 && (
                    <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
                      <div className="bg-slate-700 px-5 py-3">
                        <h3 className="text-base font-semibold text-white">Linked Maintenance</h3>
                      </div>
                      <div className="px-3 md:px-4 py-3 md:py-4">
                        <div className="space-y-2">
                          {linkedJobs.map(job => (
                            <div key={job.id} className="flex items-center p-2 bg-slate-50 rounded border border-slate-100">
                              <Wrench className="h-5 w-5 text-cyan-600 mr-2 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-slate-900 truncate">{getJobDisplayName(job)}</p>
                                <p className="text-xs text-slate-600">Interval: {job.interval} {job.unit || 'month'}{job.interval > 1 ? 's' : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  )}
                </div>
            )}

            {/* Service History Tab */}
            {selectedTab === 1 && (
              <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden mb-6">
                <div className="bg-slate-700 px-5 py-3">
                  <h3 className="text-base font-semibold text-white">Maintenance History</h3>
                </div>
                <div className="px-3 md:px-4 py-3 md:py-4">
                  <p className="mb-3 md:mb-4 text-xs text-slate-600">
                    Detailed maintenance and inspection records for this equipment item
                  </p>
                {historyForItem.length > 0 ? (
                  <div className="overflow-hidden">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 rounded-lg mb-3 text-[10px] md:text-[11px] font-medium text-slate-700 uppercase tracking-wide">
                      <div className="col-span-2">Date</div>
                      <div className="col-span-2">Job</div>
                      <div className="col-span-2">Performed by</div>
                      <div className="col-span-2">Defects</div>
                      <div className="col-span-3">Notes</div>
                      <div className="col-span-1"></div>
                    </div>
                    
                    {/* Table Rows - Clickable to view completion report */}
                    <div className="space-y-1">
                      {historyForItem
                        .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))
                        .map((record, idx) => (
                          <div
                            key={record.id || idx}
                            onClick={() => handleViewCompletionRecord(record)}
                            className="grid grid-cols-12 gap-2 px-3 py-2 text-[11px] md:text-xs border border-slate-200 rounded-lg hover:bg-slate-100 cursor-pointer transition-colors"
                          >
                            <div className="col-span-2 font-medium text-slate-900">
                              {formatHistoryDate(record.date)}
                            </div>
                            <div className="col-span-2 text-slate-800 truncate">
                              {record.title || record.checkType || record.type || 'Maintenance check'}
                              {record.completionType === 'adhoc' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                                  Ad-hoc
                                </span>
                              )}
                            </div>
                            <div className="col-span-2 text-slate-600 truncate">
                              {getCrewDisplayName(record.performedBy || record.inspector, crew)}
                            </div>
                            <div className="col-span-2">
                              {record.defectsFound ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">
                                  Defects found
                                </span>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                            <div className="col-span-3 text-slate-600 truncate">
                              {truncateNotes(record.notes)}
                            </div>
                            <div className="col-span-1 flex items-center justify-end">
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <h4 className="text-[13px] md:text-sm font-medium text-slate-900 mb-1">No Maintenance Records</h4>
                    <p className="text-[11px] md:text-xs text-slate-500">
                      No maintenance records have been logged for this item yet.
                    </p>
                  </div>
                )}
                </div>
              </section>
            )}

            {/* Documents Tab */}
            {selectedTab === 2 && (
              <DocumentsTab
                documents={documents}
                uploading={uploading}
                onUpload={handleFileUpload}
                onPreview={setPreviewDocument}
                onDownload={handleFileDownload}
                onDelete={handleFileDelete}
                onRename={handleFileRename}
                // FT-345: photo evidence wiring
                equipment={equipment}
                vesselId={currentVesselId}
                capturedBy={currentUser?.name || currentUser?.email || 'unknown'}
                onUpdateEquipment={handleUpdateEquipmentFromDocs}
              />
            )}
          </div>
        </div>
      </div>

      {/* QuickDefectModal removed - now navigates to Defects tab with RecordDefectView */}

      {/* Document Preview Modal */}
      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          onClose={() => setPreviewDocument(null)}
          onDownload={handleFileDownload}
        />
      )}

      {/* Audit Trail Delete Modal */}
      <DeleteModal />

      {/* FT-459: Custom dialog hosts — always mounted here so showConfirm/
          showAlert render immediately from this view (e.g. the serial-number
          change confirmation). Without these, the awaited confirm hangs. */}
      <ConfirmDialog />
      <AlertDialog />

      {/* Completion Report Full-Screen View */}
      {showCompletionReport && selectedCompletion && (
        <div className="fixed inset-0 z-50 bg-white">
          <MaintenanceCompletionReport
            completion={selectedCompletion}
            equipment={[equipment]}
            crew={crew}
            onBack={() => {
              setShowCompletionReport(false);
              setSelectedCompletion(null);
            }}
            onEdit={handleEditCompletionFromEquipment}
          />
        </div>
      )}
    </div>
  );
};

export default EquipmentDetailView;